import { motion, useMotionValue, useTransform, animate, useInView } from "framer-motion";
import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Quote, Award, Check } from "lucide-react";

function Counter({ value }) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest));
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      const numericValue = parseInt(value.replace(/\D/g, ""));
      animate(count, numericValue, { duration: 2, ease: "easeOut" });
    }
  }, [isInView, count, value]);

  return (
    <span ref={ref}>
      <motion.span>{rounded}</motion.span>
      {value.includes("+") && "+"}
    </span>
  );
}

export default function Om() {
  return (
    <>
      <Helmet>
        <title>Om Candra Gaarde – Stifter af Ta' JA-Hatten På!</title>
        <meta name="description" content="Mød Candra Gaarde – foredragsholder, stifter af Ta' JA-Hatten På!, udvikler af SenseBridge, erfaringsekspert og Årets Ildsjæl 2022 i Kolding." />
      </Helmet>

      <section className="pt-32 pb-20 bg-[#fafafa]">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-12 gap-24 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-6 relative"
            >
              <div className="aspect-[4/5] md:aspect-auto md:h-[500px] overflow-hidden grayscale hover:grayscale-0 transition-all duration-1000">
                <img
                  src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/d19c68f5-f69c-40ae-bab6-c731c9ce6903.jpg"
                  alt="Candra Gaarde"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-red-600/10 rounded-full blur-3xl -z-10" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-6"
            >
              <span className="text-red-600 font-semibold tracking-[0.3em] uppercase text-xs mb-8 block">Grundlægger & Taler</span>
              <h1 className="text-5xl md:text-7xl font-normal text-slate-900 leading-tight mb-12 tracking-tight">
                Mød <span className="italic font-serif text-red-600">Candra</span>
              </h1>
              <div className="space-y-8 text-lg md:text-xl text-slate-600 leading-relaxed font-light">
                <p>
                  Det er sgu da egentligt ret vildt jeg stadig er i live. Og at jeg har så meget viljestyrke, at jeg ikke vil lade mig slå ud af kurs. Jeg har en masse at byde på, som ingen andre har, og der er en mange andre derude i verden, som også har en masse ting at byde på, som jeg ikke har.
                </p>
                <p className="text-2xl md:text-3xl font-serif italic text-red-600">
                  “Jeg fortjener det bedste!”
                </p>
                <p className="text-2xl md:text-3xl font-serif italic text-red-600">
                  “Min stemme er også vigtigt”
                </p>
                <p className="text-2xl md:text-3xl font-serif italic text-red-600">
                  “ALLE mennesker kan noget!”
                </p>
                <div className="pt-12 border-t border-slate-200">
                  <Quote size={32} className="text-red-600/20 mb-6" />
                  <p className="text-xl md:text-2xl font-serif italic text-slate-900 leading-snug">
                    "Jeg omfavner at vi alle ER forskellige, diagnose eller ej. Jeg undrer mig dog til tider, hvorfor det er så vigtigt,  vi bliver puttet i en eller anden kasse… Ingen er ens! Heldigvis!"
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="bg-white border border-slate-100 p-8 md:p-12 mb-24 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/5 rounded-full blur-3xl -z-10" />
            
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Kolonne 1: Erfaring & Roller */}
              <div>
                <h3 className="text-2xl font-serif italic text-red-600 mb-6 pb-2 border-b border-slate-100">Erfaring & Roller</h3>
                <div className="space-y-3">
                  {[
                    "Stifter & indehaver af Ta´ JA-Hatten På! Selvindsigt med humor",
                    "Udvikler af SenseBridge",
                    "Foredragsholder",
                    (
                      <span>
                        Bestyrelsesmedlem
                        <br className="sm:hidden" />
                        <span className="hidden sm:inline"> </span>
                        v/ Landsforeningen{"\u00a0"}Spor
                      </span>
                    ),
                    (
                      <span>
                        Erfaringsekspert
                        <br className="sm:hidden" />
                        <span className="hidden sm:inline"> </span>
                        v/ Landsforeningen{"\u00a0"}Spor
                      </span>
                    ),
                    (
                      <span>
                        Bestyrelsesmedlem
                        <br className="sm:hidden" />
                        <span className="hidden sm:inline"> </span>
                        v/ SIND{"\u00a0"}Kolding
                      </span>
                    ),
                    (
                      <span>
                        Patient repræsentant DMPG Rusmiddel og Psykiatri
                        <br className="sm:hidden" />
                        <span className="hidden sm:inline"> </span>
                        v/ Sundhedsvæsenets Kvalitetsinstitut
                      </span>
                    ),
                    "Underviser i teaterteknikker",
                    "Årets Ildsjæl 2022 i Kolding"
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.03 }}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-50 flex items-center justify-center text-red-600 mt-0.5">
                        <Check size={12} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-700 text-base font-light leading-snug break-words">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Kolonne 2: Kurser & Uddannelser */}
              <div>
                <h3 className="text-2xl font-serif italic text-red-600 mb-6 pb-2 border-b border-slate-100">Kurser & Uddannelser</h3>
                <div className="space-y-3">
                  {[
                    (
                      <span>
                        Senfølger efter seksuelle overgreb i barndommen
                        <br className="sm:hidden" />
                        <span className="hidden sm:inline"> </span>
                        v/ Landsforeningen{"\u00a0"}Spor & CSM{"\u00a0"}Frivilligsektionen
                      </span>
                    ),
                    "Frivilliguddannelsen v/CSM",
                    "Peer-uddannet v/ Peer-Partnerskabet",
                    "Facilitator v/FriSe",
                    "Mentor v/Borger til Borger"
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.03 }}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-50 flex items-center justify-center text-red-600 mt-0.5">
                        <Check size={12} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-700 text-base font-light leading-snug">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Kolonne 3: Kurser i psykiatri */}
              <div>
                <h3 className="text-2xl font-serif italic text-red-600 mb-6 pb-2 border-b border-slate-100">Kurser i psykiatri</h3>
                <div className="space-y-3">
                  {[
                    "ADHD",
                    "Personlighedsforstyrrelse & Borderline",
                    "Bipolar sindslidelse",
                    "Kompleks PTSD m.fl."
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.03 }}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-50 flex items-center justify-center text-red-600 mt-0.5">
                        <Check size={12} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-700 text-base font-light leading-snug">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center mb-16 lg:mb-24">
            <div className="order-2 lg:order-1">
              <h2 className="text-4xl md:text-5xl font-normal text-slate-900 mb-12 tracking-tight">Passion & <span className="italic font-serif text-red-600">Nærvær</span></h2>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed font-light mb-8">
                Når jeg står på scenen, er mit mål altid at skabe et rum, hvor I kan grine, reflektere og vokse. Det handler om den energi, jeg skaber i fællesskab med jer.
              </p>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed font-light">
                Hvert foredrag er unikt, fordi hvert publikum er unikt. Jeg skræddersyr altid min formidling, så den rammer præcis der, hvor der er brug for det.
              </p>
              
              <div className="mt-12 pt-8 border-t border-slate-100">
                <p className="text-2xl md:text-3xl font-serif italic text-red-600">
                  “Man skal være psykisk stærk,<br />for at være psykisk syg”
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2 aspect-video overflow-hidden grayscale">
              <img
                src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/62254be4-d184-40bd-84ff-9da815c8ccba.jpg"
                alt="Candra i aktion"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="max-w-5xl mx-auto border-t border-slate-200 pt-16 lg:pt-20">
            <div className="text-center mb-16 lg:mb-24 max-w-3xl mx-auto space-y-6">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-normal text-slate-900 tracking-tight leading-relaxed">
                Min mission er at gøre det "unormale" mere normalt at tale om. At åbne op for muligheden, så vi kan snakke om alt det der er svært. Livet gør ondt en gang i mellem. Jeg valgte en vej som gav mig mod til at genvinde kærligheden til livet, men vigtigst af alt, til mig selv.
              </h2>
              <p className="text-2xl md:text-3xl font-serif italic text-red-600">
                “Jeg fortjener det bedste!”
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-12 lg:gap-24">
              <div className="space-y-6">
                <h3 className="text-3xl font-serif italic text-slate-900">Mødet i øjenhøjde</h3>
                <p className="text-base md:text-lg text-slate-600 leading-relaxed font-light">
                  Spørg ikke: "Hvad er der galt med dig?", men spørg: "Hvad er der sket med dig?". Min mission er at møde mennesker i øjenhøjde og opnå en fælles forståelse for den livssituation, de sidder i lige NU. Jeg taler med folk – aldrig ned til eller hen over hovedet på dem. Du er eksperten i dit liv, og jeg er eksperten i mit.
                </p>
                <p className="text-base md:text-lg text-slate-600 leading-relaxed font-light">
                  Som et løsningsorienteret menneske arbejder jeg ud fra egne erfaringer med, hvad der har virket i min recovery. Der er mange simple ting, du kan gøre for at hjælpe en sårbar patient, borger eller pårørende videre.
                </p>
              </div>
              <div className="space-y-6">
                <h3 className="text-3xl font-serif italic text-slate-900">At bryde tabuerne</h3>
                <p className="text-base md:text-lg text-slate-600 leading-relaxed font-light">
                  En brækket arm er til at forholde sig til, fordi den kan ses. Men smerte i sind og sjæl er ofte for skamfuldt, besværligt eller pinligt at tale om. Jeg er ikke bleg for at tage fat i de svære og tabubelagte emner som overgreb, misbrug og senfølger.
                </p>
                <p className="text-base md:text-lg text-slate-600 leading-relaxed font-light">
                  Ingen skal gå alene med hemmeligheder. Ensomheden kan være så enorm, at mange får det værre og selvmedicinerer. Vi skal tale om det svære og styrke kommunikationen der, hvor den ellers svigter på en travl arbejdsplads.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
